/**
 * Client-side document-extraction types + shape guard.
 *
 * POST /api/parse-tech-rider answers with one shape for every profile — a
 * PackExtraction, built from that profile's declared questions.
 * TechRiderRequirements survives as its optional `rider` block: the
 * audio-technical extraction a profile opts into, which the wizard doesn't
 * read but posts back verbatim as `parsedTechRiderJson` for the worker.
 */

export interface RiderStandCounts {
  boom?: number;
  short?: number;
  tall?: number;
}

export interface TechRiderRequirements {
  inputChannels: number | null;
  monitorMixes: number | null;
  /** Gear the production company must SUPPLY. */
  specificGear: string[];
  /**
   * Gear the band brings themselves (own modellers/DI boards, own drums).
   * Round-tripped verbatim to the worker; the frontend doesn't read it.
   */
  bandProvides?: string[];
  /**
   * Tri-state like drumsPresent: true when the band brings (or
   * conditionally offers) their own backline; null when the rider is
   * silent. Round-tripped verbatim; trims baseline backline server-side.
   */
  backlineProvidedByBand?: boolean | null;
  stands: RiderStandCounts | null;
  stageBoxNeeded: boolean;
  iemNeeded: boolean;
  drumMicsNeeded: boolean;
  /**
   * #235 tri-state: true / false when the rider is explicit about drums,
   * null when silent. Round-tripped through the public submit body so the
   * worker's bandInputsFromRider can decide HasDrums; the frontend doesn't
   * read it.
   */
  drumsPresent?: boolean | null;
  powerRequirements: string | null;
  stageLayout: string | null;
  additionalNotes: string | null;
  rawText: string;

  hasBand: boolean;
  hasDJ: boolean;
  bandSize: "small" | "medium" | "large" | null;
  hasBackline: boolean;

  /** The one identity field the technical block extracts — it prefills
   *  bandNames. Every other event fact lives on PackExtraction's core
   *  block, which every profile gets. */
  artistName: string | null;
}

/**
 * Pack-profile extraction result (#58) — the response of
 * POST /api/parse-tech-rider when the profile declares
 * `contentStep.packQuestions`. `fields` is keyed by pack-question id;
 * the backend allowlists keys to the declared questions.
 */
export interface PackExtraction {
  profileKey: string;
  fields: Record<string, boolean | string>;
  eventName: string | null;
  eventType?: string | null;
  organization: string | null;
  /** Core event fields — pre-fill the Event/Venue steps (never-overwrite).
   *  Optional so responses from an older API shape still pass the guard. */
  eventDate?: string | null;
  eventEndDate?: string | null;
  eventStartTime?: string | null;
  eventEndTime?: string | null;
  location?: string | null;
  attendance?: number | null;
  indoorOutdoor?: string | null;
  additionalNotes: string | null;
  rawText: string;
  /** The audio-technical block, present only when the profile opts in via
   *  `documentsStep.extraction.techRider`. Round-tripped verbatim to the
   *  worker as `parsedTechRiderJson`; the wizard doesn't read it. */
  rider?: TechRiderRequirements | null;
}

/** Minimum-shape check for a pack extraction blob — mirrors the
 *  isValidTechRiderShape philosophy: validate only what downstream reads. */
export function isValidPackExtractionShape(value: unknown): value is PackExtraction {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const fields = v.fields;
  if (typeof fields !== "object" || fields === null || Array.isArray(fields)) {
    return false;
  }
  return Object.values(fields).every(
    (entry) => typeof entry === "boolean" || typeof entry === "string",
  );
}
